// ── src/context/AuthContext.js ───────────────────────────────────
import React, {
  createContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext
} from "react";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";

const TOKEN_KEY         = "jwtToken";
const REFRESH_TOKEN_KEY = "jwtRefreshToken";

/* ------------------------------------------------------------------ */
export const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx)
    throw new Error("useAuth must be used within AuthContextProvider");
  return ctx;
};

/* ------------------------------------------------------------------ */
export const AuthContextProvider = ({ children }) => {
  const [authData, setAuthData] = useState({
    token: null,
    refreshToken: null,
    userId: null,
    email: null,
    roleId: null,
    role: null,
    hotelId: null,
    hotelName: null,
    firstName: null,
    lastName: null,
    profilePicture: null,
    isKycCompleted: false,
    isEmailConfirmed: false,
    kycStatus: "NotSubmitted",
    trialTime: 0,
    expiresAt: null,
    isInitialized: false // Added initialization flag
  });
  const [loading, setLoading] = useState(true);
  const navigate              = useNavigate();

  /* refs to avoid stale closures in setInterval */
  const tokenRef   = useRef(null);
  const expiresRef = useRef(null);

  /* ───────────────── update helper (new) ───────────────────────── */
  const updateAuthData = useCallback((partial) => {
    setAuthData((prev) => {
      const next = { ...prev, ...partial };

      /* keep axios header and localStorage in sync */
      if (partial.token) {
        axiosInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${partial.token}`;
        localStorage.setItem(TOKEN_KEY, partial.token);
      }
      if (partial.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, partial.refreshToken);
      }
      return next;
    });
  }, []);

  /* ───────────────────────── logout ───────────────────────────── */
  const handleLogout = useCallback(() => {
    setAuthData({
      token: null,
      refreshToken: null,
      userId: null,
      email: null,
      roleId: null,
      role: null,
      hotelId: null,
      hotelName: null,
      firstName: null,
      lastName: null,
      profilePicture: null,
      isKycCompleted: false,
      isEmailConfirmed: false,
      kycStatus: "NotSubmitted",
      trialTime: 0,
      expiresAt: null,
      isInitialized: true // Mark as initialized even on logout
    });
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    delete axiosInstance.defaults.headers.common["Authorization"];
    navigate("/login", { replace: true });
    toast.info("Logged out successfully");
  }, [navigate]);

  /* helper to pull a single role from array|string */
  const extractRole = (raw) =>
    !raw ? null : Array.isArray(raw) ? raw[0] ?? null : raw;

  /* ───────────────────────── login ────────────────────────────── */
  const handleLogin = useCallback(
    (payload) => {
      try {
        const accessToken =
          payload.accessToken || payload.token || payload?.AccessToken;
        if (!accessToken) throw new Error("No access token received");

        const refreshToken =
          payload.refreshToken || payload.RefreshToken || null;
        const userObj   = payload.user || {};
        const decoded   = jwtDecode(accessToken);

        const role =
          extractRole(userObj.role) ||
          extractRole(decoded.role) ||
          extractRole(decoded.roles);

        const boolFromDecoded = (v) =>
          v === true || v === "true" ? true : false;

        const expiresAt = decoded.exp ? new Date(decoded.exp * 1000) : null;

        updateAuthData({
          token: accessToken,
          refreshToken,
          roleId: userObj.roleId || decoded.roleId || null,
          userId: userObj.id || decoded.sub || null,
          email: userObj.email || decoded.email || null,
          role,
          hotelId: userObj.hotelId || decoded.hotelId || null,
          hotelName: userObj.hotelName || decoded.hotelName || null,
          firstName:
            userObj.firstName ??
            (Array.isArray(decoded.firstName)
              ? decoded.firstName[0]
              : decoded.firstName) ??
            null,
          lastName:
            userObj.lastName ??
            (Array.isArray(decoded.lastName)
              ? decoded.lastName[0]
              : decoded.lastName) ??
            null,
          profilePicture:
            userObj.profilePicture || decoded.profilePicture || null,
          isKycCompleted:
            userObj.isKycCompleted ??
            boolFromDecoded(decoded.isKycCompleted),
          isEmailConfirmed:
            userObj.isEmailConfirmed ??
            boolFromDecoded(decoded.isEmailConfirmed),
          kycStatus: userObj.kycStatus || decoded.kycStatus || "NotSubmitted",
          trialTime:
            userObj.trialTime ??
            (decoded.trialTime ? parseInt(decoded.trialTime, 10) : 0),
          expiresAt,
          isInitialized: true // Mark as initialized after successful login
        });

        toast.success("Login successful");
      } catch (err) {
        console.error("Login processing error:", err);
        toast.error("Failed to process login");
        handleLogout();
      }
    },
    [updateAuthData, handleLogout]
  );

  /* ───────────────────── refresh token ────────────────────────── */
  const handleRefreshToken = useCallback(
    async () => {
      try {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) throw new Error("No refresh token available");

        const resp = await axiosInstance.post("/auth/refresh-token", {
          refreshToken
        });

        if (resp.data?.accessToken) {
          handleLogin(resp.data);
          return true;
        }
        return false;
      } catch (err) {
        console.error("Token refresh failed:", err);
        handleLogout();
        return false;
      }
    },
    [handleLogin, handleLogout]
  );

  const isTokenExpired = useCallback(
    (expiresAt) => !expiresAt || new Date() > new Date(expiresAt),
    []
  );

  /* ───────────────────── initialize on mount ──────────────────── */
  const initializeAuth = useCallback(async () => {
    const token        = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!token) {
      setAuthData(prev => ({ ...prev, isInitialized: true }));
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const expired = new Date() > new Date(decoded.exp * 1000);

      if (expired) {
        const ok = await handleRefreshToken();
        if (!ok) {
          handleLogout();
          return;
        }
      } else {
        handleLogin({
          accessToken: token,
          refreshToken,
          user: {
            id: decoded.sub,
            email: decoded.email,
            role: extractRole(decoded.role) || extractRole(decoded.roles),
            hotelId: decoded.hotelId,
            hotelName: decoded.hotelName,
            firstName: Array.isArray(decoded.firstName)
              ? decoded.firstName[0]
              : decoded.firstName,
            lastName: Array.isArray(decoded.lastName)
              ? decoded.lastName[0]
              : decoded.lastName,
            profilePicture: decoded.profilePicture,
            isKycCompleted: decoded.isKycCompleted === "true",
            isEmailConfirmed: decoded.isEmailConfirmed === "true",
            kycStatus: decoded.kycStatus || "NotSubmitted",
            trialTime: decoded.trialTime
          }
        });
      }
    } catch (err) {
      console.error("Auth initialization error:", err);
      handleLogout();
    } finally {
      setAuthData(prev => ({ ...prev, isInitialized: true }));
      setLoading(false);
    }
  }, [handleLogin, handleLogout, handleRefreshToken]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  /* keep refs current */
  useEffect(() => {
    tokenRef.current   = authData.token;
    expiresRef.current = authData.expiresAt;
  }, [authData.token, authData.expiresAt]);

  /* periodic refresh every 5 min */
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        tokenRef.current &&
        isTokenExpired(expiresRef.current)
      ) {
        handleRefreshToken().catch(console.error);
      }
    }, 300_000);
    return () => clearInterval(interval);
  }, [handleRefreshToken, isTokenExpired]);

  /* ───────────────────── provider value ───────────────────────── */
  return (
    <AuthContext.Provider
      value={{
        authData,
        updateAuthData,
        handleLogin,
        handleLogout,
        handleRefreshToken,
        loading,
        isInitialized: authData.isInitialized, // Expose initialization state
        isExpired: () => isTokenExpired(authData.expiresAt),
        isAuthenticated: !!authData.token && !isTokenExpired(authData.expiresAt)
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};